import React, { useState, useEffect, useCallback } from 'react';
import { FilterProvider, useFilters } from './FilterContext';
import { MastersProvider } from './MastersContext';
import GlobalFilterBar from './components/GlobalFilterBar';
import Overview from './Overview';
import Planning from './Planning';
import SampleLog from './SampleLog';
import SampleAgeing from './SampleAgeing';
import Masters from './Masters';
import NewSampleDrawer from './NewSampleDrawer';
import { fetchOverview } from './api';

const HEADER_HEIGHT = 42;

const TAB_DEFS = [
  { key: 'overview', label: 'Overview' },
  { key: 'planning', label: 'Planning' },
  { key: 'log', label: 'Sample Log' },
  { key: 'ageing', label: 'Sample Ageing' },
  { key: 'masters', label: 'Masters' },
];

const RndInner = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerStats, setHeaderStats] = useState({ total: 0, pending: 0, running: 0, avgTat: '—' });
  const [clock, setClock] = useState('');
  const { buildQueryParams } = useFilters(); // eslint-disable-line no-unused-vars

  // Live clock
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('en-GB', { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Header stats
  const loadHeaderStats = useCallback(async () => {
    try {
      const data = await fetchOverview({});
      const kpi = data.kpis || {};
      setHeaderStats({
        total: kpi.total || 0,
        pending: kpi.pending || 0,
        running: kpi.running || 0,
        avgTat: kpi.avgTat != null ? `${Number(kpi.avgTat).toFixed(1)}d` : '—',
      });
    } catch (e) {
      console.error('Failed to load header stats:', e);
    }
  }, []);

  useEffect(() => { loadHeaderStats(); }, [loadHeaderStats]);

  const handleSampleCreated = () => {
    loadHeaderStats();
    setActiveTab('log');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return <Overview />;
      case 'planning': return <Planning />;
      case 'log': return <SampleLog />;
      case 'ageing': return <SampleAgeing />;
      case 'masters': return <Masters />;
      default: return <Overview />;
    }
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', system-ui, sans-serif",
      backgroundColor: '#eef0f4',
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      color: '#111827',
      fontSize: 12,
    }}>
      {/* App Header */}
      <div style={{
        height: HEADER_HEIGHT,
        backgroundColor: '#1a3353',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        color: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,.28)',
        zIndex: 200,
        flexShrink: 0,
      }}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', backgroundColor: '#34d399',
            boxShadow: '0 0 0 2px rgba(52,211,153,.3)', display: 'inline-block',
          }} />
          <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: -0.3 }}>AR&D Lab</span>
          <span style={{
            fontSize: 8, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
            backgroundColor: 'rgba(255,255,255,.12)', padding: '2px 7px', borderRadius: 3,
          }}>
            INWARD REGISTER
          </span>
        </div>

        {/* Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Stat: Total */}
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.45)', lineHeight: 1.2 }}>Total</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700 }}>{headerStats.total}</div>
          </div>
          {/* Stat: Pending */}
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.45)', lineHeight: 1.2 }}>Pending</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: '#fbbf24' }}>{headerStats.pending}</div>
          </div>
          {/* Stat: Running */}
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.45)', lineHeight: 1.2 }}>Running</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: '#34d399' }}>{headerStats.running}</div>
          </div>
          {/* Stat: Avg TAT */}
          <div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,.45)', lineHeight: 1.2 }}>Avg TAT</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700, color: '#60a5fa' }}>{headerStats.avgTat}</div>
          </div>

          {/* Separator */}
          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,.15)' }} />

          {/* Clock */}
          <span style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
            color: 'rgba(255,255,255,.4)',
          }}>
            {clock}
          </span>

          {/* New Sample button */}
          <button
            onClick={() => setDrawerOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              backgroundColor: '#2563eb', color: '#fff', border: 'none',
              padding: '5px 12px', borderRadius: 5, fontFamily: "'DM Sans', sans-serif",
              fontSize: 11, fontWeight: 600, cursor: 'pointer', transition: 'background 0.13s',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1d4ed8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2563eb'}
          >
            + New Sample
          </button>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        background: '#1e3d63', display: 'flex', padding: '0 16px', flexShrink: 0,
      }}>
        {TAB_DEFS.map(tab => (
          <div
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '7px 15px 6px',
              fontSize: 11, fontWeight: 600,
              color: activeTab === tab.key ? '#fff' : 'rgba(255,255,255,.45)',
              cursor: 'pointer',
              borderBottom: activeTab === tab.key ? '2px solid #60a5fa' : '2px solid transparent',
              transition: 'all 0.13s',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'rgba(255,255,255,.75)'; }}
            onMouseLeave={e => { if (activeTab !== tab.key) e.currentTarget.style.color = 'rgba(255,255,255,.45)'; }}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <GlobalFilterBar />

      {/* Tab Content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {renderTabContent()}
      </div>

      {/* Drawer */}
      <NewSampleDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={handleSampleCreated}
      />
    </div>
  );
};

const RndSampleTracking = () => (
  <FilterProvider>
    <MastersProvider>
      <RndInner />
    </MastersProvider>
  </FilterProvider>
);

export default RndSampleTracking;
